import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FiAlertTriangle, FiArrowLeft, FiClock } from 'react-icons/fi';

import { getFreshness, humanizeAge } from '../../utils/deviceData';

// Sticky top bar for the device detail page.
//
// Shows three stripes of information left-to-right:
//   1. Back button + breadcrumb (Devices › <Name>)
//   2. Live status pill (online/offline + last_time_seen age)
//   3. Freshness badge for device_data + any in-flight command/feedback
//
// The component is purely presentational — all data comes from props.

export default function HeaderBar({ mac, device }) {
    const navigate = useNavigate();

    // Display name precedence: explicit name → MAC. Customers think in names.
    const displayName = device?.name || mac;

    // Real-time online/offline. `status` is a flat field at the device root,
    // updated by the 10-second heartbeat — independent of device_data freshness.
    const isOnline = device?.status === 'online';

    // `last_time_seen` arrives as a string from RTDB; coerce before doing math.
    const lastSeenMs = device?.last_time_seen ? Number(device.last_time_seen) : null;
    const lastSeenAge = lastSeenMs ? humanizeAge(Math.max(0, Date.now() - lastSeenMs)) : null;

    // device_data freshness — different from `last_time_seen`. The heartbeat
    // can be alive while the rich diagnostics block is stale (10-min cadence).
    const freshness = getFreshness(device?.device_data?.updated_at);

    // While a command is in flight we surface it. `feedback` flips back to
    // success/error; treat empty string as "idle".
    const command = device?.command || '';
    const feedback = device?.feedback || '';

    return (
        <div className="sticky top-0 z-30 bg-surface-page/95 backdrop-blur border-b border-white/[0.06]">
            <div className="px-6 py-3 flex items-center gap-4 flex-wrap">
                {/* 1 — Back + breadcrumb */}
                <button
                    onClick={() => navigate('/devices')}
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/[0.04] hover:bg-white/[0.08] text-sm transition"
                >
                    <FiArrowLeft /> Devices
                </button>
                <div className="flex items-center gap-2 text-sm text-gray-400">
                    <span>›</span>
                    <span className="text-white font-medium">{displayName}</span>
                    {device?.name && (
                        <span className="text-xs text-gray-500 font-mono ml-2">{mac}</span>
                    )}
                </div>

                {/* 2 — Status pill + freshness, pushed right with this spacer.
                    Remote Control and Reboot buttons moved to the Overview
                    tab's Controls card so the header stays focused on
                    state-at-a-glance. */}
                <div className="ml-auto flex items-center gap-3 flex-wrap">
                    <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium ${
                        isOnline ? 'bg-green-500/15 text-green-400' : 'bg-red-500/15 text-red-400'
                    }`}>
                        <span className={`w-2 h-2 rounded-full ${isOnline ? 'bg-green-500' : 'bg-red-500'}`} />
                        {isOnline ? 'Online' : 'Offline'}
                        {lastSeenAge && (
                            <span className="text-gray-400 font-normal">· seen {lastSeenAge}</span>
                        )}
                    </span>

                    {/* In-flight command indicator. Only render if we actually
                        have something to say so the bar stays calm at rest. */}
                    {command && (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-yellow-500/15 text-yellow-400 text-xs font-medium">
                            <FiClock /> command: {command}
                        </span>
                    )}
                    {!command && feedback === 'error' && (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-red-500/15 text-red-400 text-xs font-medium">
                            <FiAlertTriangle /> last command failed
                        </span>
                    )}

                    {/* 3 — device_data freshness. Stale gets a yellow badge so
                        the user knows the metrics they're reading might lag. */}
                    <FreshnessBadge freshness={freshness} />
                </div>
            </div>
        </div>
    );
}

function FreshnessBadge({ freshness }) {
    if (freshness.isMissing) {
        return (
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/[0.05] text-gray-400 text-xs">
                <FiClock /> initializing…
            </span>
        );
    }
    const tone = freshness.isStale
        ? 'bg-yellow-500/15 text-yellow-400'
        : 'bg-white/[0.05] text-gray-300';
    return (
        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs ${tone}`}>
            <FiClock />
            {freshness.isStale ? 'stale · ' : ''}updated {freshness.label}
        </span>
    );
}
